import type { Middleware } from "@reduxjs/toolkit";
import { z } from "zod";
import Session from "users/__session/session";
import { principalChanged } from "users/__session/principal-change";
import {
  ProposalContentOperationZ,
  ProposalReviewerChangeZ,
  SaveProposalDraftCommandZ,
  type ProposalContentOperation,
  type ProposalReviewerChange,
  type SaveProposalDraftCommand,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import type { ProposalCollaborationStoreState } from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import { decodeProposalPointerSegment } from "work/proposals/negotiation/proposal-operation-projector";

export const PROPOSAL_COLLABORATION_STORAGE_KEY = "attorneyhub.proposals.collaborationQueue.v1";

export type ProposalCollaborationStorageScope = {
  userId: string;
  legalEntityId: string;
  party: "Client" | "Vendor";
};

export type StoredProposalCollaborationQueue = {
  pendingOperations: ProposalContentOperation[];
  pendingReviewerChanges: ProposalReviewerChange[];
  inFlightCommands: Record<string, SaveProposalDraftCommand | undefined>;
};

export type StoredProposalCollaborationQueues = Record<string, StoredProposalCollaborationQueue | undefined>;

type StorageLike = Pick<Storage, "getItem" | "removeItem" | "setItem">;

const StoredQueueZ = z.object({
  pendingOperations: z.array(ProposalContentOperationZ),
  pendingReviewerChanges: z.array(ProposalReviewerChangeZ),
  inFlightCommands: z.record(z.string(), SaveProposalDraftCommandZ.optional()),
});

const StoredQueuesZ = z.object({
  version: z.literal(1),
  queues: z.record(z.string(), StoredQueueZ.optional()),
});

export function extractProposalCollaborationQueues(
  state: ProposalCollaborationStoreState,
): StoredProposalCollaborationQueues {
  return Object.fromEntries(
    Object.entries(state.byNegotiationId).flatMap(([negotiationId, entry]) => {
      if (!entry) return [];
      const inFlightCommands = Object.fromEntries(
        Object.entries(entry.inFlightCommands).filter((entry): entry is [string, SaveProposalDraftCommand] =>
          Boolean(entry[1]),
        ),
      );
      if (
        entry.pendingOperations.length === 0 &&
        entry.pendingReviewerChanges.length === 0 &&
        Object.keys(inFlightCommands).length === 0
      )
        return [];

      return [
        [
          negotiationId,
          {
            pendingOperations: entry.pendingOperations,
            pendingReviewerChanges: entry.pendingReviewerChanges,
            inFlightCommands,
          },
        ],
      ];
    }),
  );
}

export function loadProposalCollaborationQueues(
  storage = getBrowserLocalStorage(),
  scope = getCurrentStorageScope(),
): StoredProposalCollaborationQueues {
  if (!storage || !scope) return {};

  try {
    const serialized = storage.getItem(proposalCollaborationStorageKey(scope));
    if (!serialized) return {};
    const parsed = StoredQueuesZ.safeParse(JSON.parse(serialized));
    if (!parsed.success) return {};

    const sanitized = sanitizeStoredQueues(parsed.data.queues);
    if (sanitized.changed) {
      const storageKey = proposalCollaborationStorageKey(scope);
      if (Object.keys(sanitized.queues).length === 0) storage.removeItem(storageKey);
      else storage.setItem(storageKey, JSON.stringify({ version: 1, queues: sanitized.queues }));
    }
    return sanitized.queues;
  } catch {
    return {};
  }
}

/** Drops vestigial snapshot-derived operations from pre-canonical v1 caches. */
function sanitizeStoredQueues(queues: StoredProposalCollaborationQueues): {
  queues: StoredProposalCollaborationQueues;
  changed: boolean;
} {
  let changed = false;
  const sanitizedQueues: StoredProposalCollaborationQueues = {};

  for (const [negotiationId, queue] of Object.entries(queues)) {
    if (!queue) continue;
    const pendingOperations = queue.pendingOperations.filter((operation) => {
      const keep = !targetsLegacyUnresolvedFields(operation);
      if (!keep) changed = true;
      return keep;
    });
    const inFlightCommands = Object.fromEntries(
      Object.entries(queue.inFlightCommands).flatMap(([commandId, command]) => {
        if (!command) return [];
        const operations = command.operations.filter((operation) => {
          const keep = !targetsLegacyUnresolvedFields(operation);
          if (!keep) changed = true;
          return keep;
        });
        if (operations.length === 0 && command.reviewerChanges.length === 0) {
          changed = true;
          return [];
        }
        return [[commandId, { ...command, operations }]];
      }),
    );
    if (
      pendingOperations.length === 0 &&
      queue.pendingReviewerChanges.length === 0 &&
      Object.keys(inFlightCommands).length === 0
    ) {
      if (
        queue.pendingOperations.length > 0 ||
        queue.pendingReviewerChanges.length > 0 ||
        Object.keys(queue.inFlightCommands).length > 0
      ) {
        changed = true;
      }
      continue;
    }
    sanitizedQueues[negotiationId] = {
      pendingOperations,
      pendingReviewerChanges: queue.pendingReviewerChanges,
      inFlightCommands,
    };
  }

  return { queues: sanitizedQueues, changed };
}

function targetsLegacyUnresolvedFields(operation: ProposalContentOperation): boolean {
  return operation.path
    .split("/")
    .slice(1)
    .map(decodeProposalPointerSegment)
    .includes("unresolvedFields");
}

/**
 * Remembers the last blob this process believes is in storage for a given key, so a second
 * consecutive write for the same key can skip re-reading and re-validating it. `rawSerialized` is
 * the exact string last written (or `null` for "removed") — before trusting `queues`, the write path
 * re-reads that one string (a plain `getItem`, not a parse) and confirms it still matches. Any other
 * writer (another tab, a test seeding `localStorage` directly, a future negotiation entry) changes the
 * stored string and the guard naturally falls back to a real validated read.
 */
type CachedProposalCollaborationQueues = {
  storageKey: string;
  rawSerialized: string | null;
  queues: StoredProposalCollaborationQueues;
};

let cachedQueues: CachedProposalCollaborationQueues | undefined;

function readPriorQueuesForWrite(
  storage: StorageLike,
  storageKey: string,
  scope: ProposalCollaborationStorageScope,
): StoredProposalCollaborationQueues {
  if (cachedQueues?.storageKey === storageKey) {
    const currentRaw = storage.getItem(storageKey) ?? null;
    if (currentRaw === cachedQueues.rawSerialized) return cachedQueues.queues;
  }
  return loadProposalCollaborationQueues(storage, scope);
}

export function saveProposalCollaborationQueues(
  state: ProposalCollaborationStoreState,
  storage = getBrowserLocalStorage(),
  // A live viewer scope is already exactly what session-derived scope resolution would produce (it is
  // where that string came from), so prefer parsing it over re-deriving it from the session on every
  // dispatched action — `getCurrentStorageScope()` re-reads and re-parses the stored session on every
  // call. Isolated reducer/test stores with no active scope fall back to session resolution.
  scope = proposalCollaborationStorageScopeFromViewerScope(state.activeViewerScope) ?? getCurrentStorageScope(),
): void {
  if (!storage || !scope) return;
  if (state.activeViewerScope && state.activeViewerScope !== proposalCollaborationViewerScope(scope)) return;

  const storageKey = proposalCollaborationStorageKey(scope);
  try {
    const queues = { ...readPriorQueuesForWrite(storage, storageKey, scope) };
    const currentQueues = extractProposalCollaborationQueues(state);
    const hydratedNegotiationIds = Object.keys(state.hydratedNegotiationIds ?? {});

    // Only replace queues that this viewer has actually hydrated. This keeps a
    // cold page load from deleting another unopened proposal's durable queue,
    // and keeps the first authoritative envelope from deleting its own queue
    // before React has had a chance to hydrate it.
    for (const negotiationId of hydratedNegotiationIds) {
      const currentQueue = currentQueues[negotiationId];
      if (currentQueue) queues[negotiationId] = currentQueue;
      else delete queues[negotiationId];
    }

    // Backward-compatible behavior for isolated reducer/test stores that do not
    // activate a viewer scope. The application always activates a scope first.
    if (!state.activeViewerScope) {
      for (const negotiationId of Object.keys(queues)) delete queues[negotiationId];
      Object.assign(queues, currentQueues);
    }

    if (Object.keys(queues).length === 0) {
      storage.removeItem(storageKey);
      cachedQueues = { storageKey, rawSerialized: null, queues };
      return;
    }
    const serialized = JSON.stringify({ version: 1, queues });
    storage.setItem(storageKey, serialized);
    cachedQueues = { storageKey, rawSerialized: serialized, queues };
  } catch {
    // Storage is an optimization. Redux retains the queue when storage is unavailable. The cache is
    // now of unknown accuracy for this key, so drop it rather than risk trusting a partial write.
    if (cachedQueues?.storageKey === storageKey) cachedQueues = undefined;
  }
}

export const proposalCollaborationPersistenceMiddleware: Middleware = ({ getState }) => (next) => (action) => {
  const previousState = getCollaborationState(getState());
  const result = next(action);
  const nextState = getCollaborationState(getState());

  // A principal switch clears the in-memory projection, but the durable queue is scoped to its
  // original user/legal-entity/party. Do not persist the empty reset state over that owner's unsent
  // work. Activating that viewer scope again will hydrate its queue normally.
  if (principalChanged.match(action)) return result;

  if (nextState && nextState !== previousState) saveProposalCollaborationQueues(nextState);
  return result;
};

function getCollaborationState(rootState: unknown): ProposalCollaborationStoreState | undefined {
  if (!isRecord(rootState) || !isRecord(rootState.proposalCollaboration)) return undefined;
  const state = rootState.proposalCollaboration;
  return isRecord(state.byNegotiationId) ? (state as ProposalCollaborationStoreState) : undefined;
}

function getBrowserLocalStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

export function proposalCollaborationStorageKey(scope: ProposalCollaborationStorageScope): string {
  return `${PROPOSAL_COLLABORATION_STORAGE_KEY}.${proposalCollaborationViewerScope(scope)}`;
}

export function proposalCollaborationViewerScope(scope: ProposalCollaborationStorageScope): string {
  return `${scope.userId}.${scope.legalEntityId}.${scope.party}`;
}

export function proposalCollaborationStorageScopeFromSession(
  session: Pick<Session, "context" | "user">,
): ProposalCollaborationStorageScope | undefined {
  const userId = session.user?.id?.value;
  const legalEntityId = session.user?.legalEntityId?.value;
  if (!userId || !legalEntityId) return undefined;
  return {
    userId,
    legalEntityId,
    party: session.context?.viewingAsVendor ? "Vendor" : "Client",
  };
}

/**
 * Recovers the scope object from an already-activated viewer scope string (`userId.legalEntityId.party`,
 * as built by `proposalCollaborationViewerScope`), so the persistence middleware need not re-derive it
 * from the session on every dispatched action. Safe because neither a Guid nor `"Client"`/`"Vendor"`
 * ever contains a `.`, so the split is unambiguous.
 */
function proposalCollaborationStorageScopeFromViewerScope(
  viewerScope: string | undefined,
): ProposalCollaborationStorageScope | undefined {
  if (!viewerScope) return undefined;
  const [userId, legalEntityId, party] = viewerScope.split(".");
  if (!userId || !legalEntityId || (party !== "Client" && party !== "Vendor")) return undefined;
  return { userId, legalEntityId, party };
}

function getCurrentStorageScope(): ProposalCollaborationStorageScope | undefined {
  try {
    const session = Session.loadFromStorage(undefined as never);
    return proposalCollaborationStorageScopeFromSession(session);
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
